import { StoreBaseImplementation } from '@/shared/classes/store-base-implementation';
import {
  isRentAnalyzerTierEligible,
  parseRentAnalyzerTierRequirement,
} from '@/shared/helpers/rent-analyzer';
import { IStateStrict, IAction } from '@/shared/interfaces/store-base';
import { IRentEstimationResponse } from '@/shared/interfaces/rent-estimation';
import { getCurrentClientInfinityAddonConfig } from '@/shared/helpers/infinity-addon-config';
import { adminRequest } from '@/shared/request-configs/admin-request';

interface IInfinityPromotedListing {
  buildingId: number;
  tierOrder: number;
}

export interface IRentAnalyzerState extends IStateStrict<IRentEstimationResponse> {
  requiredTier: number | null;
  infinityTiers: Record<number, number>;
  rentEstimation: IRentEstimationResponse | null;
  loading: boolean;
  error: string;
}

const base = new StoreBaseImplementation<IRentEstimationResponse>({
  adminGatewayResourcePath: '/rent-estimation',
  defaultGetListFilters: {},
  defaultGetSingleFilters: {},
});

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    requiredTier: null,
    infinityTiers: {},
    rentEstimation: null,
    loading: false,
    error: '',
  } as IRentAnalyzerState,

  getters: {
    ...base.getters,

    isEligible: (state: IRentAnalyzerState) => (buildingId: number): boolean => {
      const buildingTier = state.infinityTiers[buildingId];
      return isRentAnalyzerTierEligible(buildingTier, state.requiredTier);
    },
    rentEstimation: (state: IRentAnalyzerState): IRentEstimationResponse | null => state.rentEstimation,
    loading: (state: IRentAnalyzerState): boolean => state.loading,
    error: (state: IRentAnalyzerState): string => state.error,
  },

  mutations: {
    ...base.mutations,

    setRequiredTier(state: IRentAnalyzerState, tier: number) {
      state.requiredTier = tier;
    },
    setInfinityTiers(state: IRentAnalyzerState, tiers: Record<number, number>) {
      state.infinityTiers = tiers;
    },
    setRentEstimation(state: IRentAnalyzerState, data: IRentEstimationResponse | null) {
      state.rentEstimation = data;
    },
    setLoading(state: IRentAnalyzerState, loading: boolean) {
      state.loading = loading;
    },
    setError(state: IRentAnalyzerState, error: string) {
      state.error = error;
    },
    reset(state: IRentAnalyzerState) {
      state.requiredTier = null;
      state.infinityTiers = {};
      state.rentEstimation = null;
      state.loading = false;
      state.error = '';
    },
  },

  actions: {
    ...base.actions,

    async loadEligibility(
      ctx: IAction<IRentAnalyzerState>,
      payload: { clientId: number | string },
    ): Promise<void> {
      try {
        const [config, tiersRes] = await Promise.all([
          Promise.resolve(getCurrentClientInfinityAddonConfig(payload.clientId)),
          adminRequest.get(
            `/client-api/infinity-promoted-listings?clientIds=${payload.clientId}`,
          ),
        ]);

        let requiredTier: number | null = null;

        // Check that rent analyzer key is defined AND that the addons are active.
        if (config?.rentAnalyzer !== undefined && config?.active) {
          requiredTier = parseRentAnalyzerTierRequirement(config.rentAnalyzer);
        }

        ctx.commit('setRequiredTier', requiredTier);

        // Parse infinity tiers per building
        const tiers: Record<number, number> = {};
        const data = tiersRes?.data;
        if (Array.isArray(data)) {
          data.forEach((item: IInfinityPromotedListing) => {
            tiers[item.buildingId] = item.tierOrder;
          });
        }
        ctx.commit('setInfinityTiers', tiers);
      } catch (e) {
        console.error('Failed to load rent analyzer eligibility:', e);
        ctx.commit('setRequiredTier', null);
        ctx.commit('setInfinityTiers', {});
      }
    },

    async fetchEstimation(
      ctx: IAction<IRentAnalyzerState>,
      payload: {
        bed?: number;
        bath?: number;
        sqft?: number;
        postal?: string;
        city?: string;
        province?: string;
      },
    ): Promise<void> {
      ctx.commit('setLoading', true);
      ctx.commit('setError', '');
      ctx.commit('setRentEstimation', null);

      try {
        const body: Record<string, string | number> = {};
        if (payload.bed != null) body.bed = payload.bed;
        if (payload.bath != null) body.bath = payload.bath;
        if (payload.sqft) body.sqft = payload.sqft;
        if (payload.postal) body.postal = payload.postal;
        if (payload.city) body.city = payload.city;
        if (payload.province) body.province = payload.province;

        const response = await adminRequest.post<IRentEstimationResponse>('/rent-estimation/estimate', body);

        ctx.commit('setRentEstimation', response.data);
      } catch (e) {
        console.error('Rent estimation error:', e);
        ctx.commit('setError', 'Unable to fetch rent estimation data. Please try again later.');
      } finally {
        ctx.commit('setLoading', false);
      }
    },
  },
};
